<template>
  <VaLayout v-if="breakpoint.lgUp" class="h-screen bg-[var(--va-background-secondary)]">
    <template #left>
      <RouterLink
        class="bg-primary h-full flex items-center justify-center"
        style="width: 35vw"
        to="/"
        aria-label="Visit homepage"
      >
        <!--
          White text on this panel (bg-primary, now #F77E2D) only clears ~2.6:1 — well below the
          3:1 WCAG needs even for text this large/bold. Dark ink clears ~6.8:1 instead; the old
          indigo primary was dark enough for white text to work, the new orange isn't.
        -->
        <AppWordmark :height="36" start="#17171A" />
      </RouterLink>
    </template>
    <template #content>
      <div class="auth-background h-full" :style="backgroundStyle">
        <main class="h-full flex items-center justify-center mx-auto max-w-[420px]">
          <div class="auth-card">
            <RouterView />
          </div>
        </main>
      </div>
    </template>
  </VaLayout>

  <VaLayout v-else class="h-screen bg-[var(--va-background-secondary)]">
    <template #content>
      <!--
        `h-full` here is what makes `h-full` on <main> below mean anything — without it this
        div had no defined height of its own, so <main>'s "100% of my parent" resolved against
        nothing and the form just sat at its natural content height (top-aligned, empty space
        below) instead of filling/centering in the viewport.
      -->
      <div class="auth-background h-full p-4" :style="backgroundStyle">
        <main class="h-full flex items-center justify-center mx-auto max-w-[420px]">
          <!-- `items-center` (was `items-start`) so the wordmark — narrower than the form
               fields below it, which fill the available width themselves — centers horizontally
               too, instead of hugging the left edge. -->
          <div class="flex flex-col items-center w-full">
            <RouterLink class="py-4" to="/" aria-label="Visit homepage">
              <AppWordmark class="mb-2 auth-wordmark" :height="24" />
            </RouterLink>
            <div class="auth-card w-full">
              <RouterView />
            </div>
          </div>
        </main>
      </div>
    </template>
  </VaLayout>
</template>

<script lang="ts" setup>
import { useBreakpoint } from 'vuestic-ui'
import AppWordmark from '../components/AppWordmark.vue'
import loginBackground from '../assets/login-background.webp'

const breakpoint = useBreakpoint()

// Bound via :style (rather than CSS v-bind() inside the <style> block) so Vite's asset pipeline
// resolves/fingerprints the import the same straightforward way as any other imported image —
// no risk of the SCSS compiler mangling a `url(v-bind(...))` token before Vue's compiler gets to it.
const backgroundStyle = { backgroundImage: `url(${loginBackground})` }
</script>

<style lang="scss" scoped>
// Background image for the panel that holds the login/recover-password/check-email forms — both
// the desktop right-hand panel and the full mobile/tablet panel (the left orange panel with the
// wordmark, desktop-only, is untouched — see the `bg-primary` RouterLink above). The image itself
// is set via the `backgroundStyle` binding on the template element (script setup, above) rather
// than here, so it goes through Vite's normal asset import/fingerprinting.
.auth-background {
  position: relative;
  background-size: cover;
  background-position: center;

  // A LIGHT wash of the layout's own base surface tone (--va-background-secondary) over the
  // photo — purely an aesthetic tint now, not the legibility mechanism. It used to be strong
  // enough (88%→72%) to carry legibility for both the card and the mobile wordmark on its own,
  // but that meant the card's own translucent/blurred background (below) was compositing on top
  // of an already near-opaque layer: two "mostly white" layers stacked make a fully-white result,
  // which is exactly why the glass effect was reported as invisible — the card LOOKED opaque
  // because, once combined with this overlay, it effectively was. Legibility is now the card's
  // job alone (its own opacity + blur), and the mobile wordmark's job alone (its own text-shadow,
  // see .auth-wordmark below); this overlay only needs to keep the raw photo from feeling too
  // stark, so it can stay light enough for the card's glass effect to actually show something.
  // Recomputes automatically for dark mode since it's built from the theme token, not a fixed hex.
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      160deg,
      color-mix(in srgb, var(--va-background-secondary) 30%, transparent) 0%,
      color-mix(in srgb, var(--va-background-secondary) 15%, transparent) 100%
    );
  }

  // Every direct child (the <main> centering wrapper) needs to sit above the ::before overlay.
  > * {
    position: relative;
    z-index: 1;
  }
}

// Mobile/tablet only — this wordmark sits directly on the (now much lighter) tinted photo, with
// no card behind it, so with the overlay above turned down it needs its own legibility cushion
// instead of leaning on the overlay's old strength. A soft shadow rather than a solid background
// chip, to match the same "light touch" as everything else here.
.auth-wordmark {
  text-shadow: 0 2px 12px color-mix(in srgb, var(--va-background-secondary) 90%, transparent);
}

// The form itself (Login.vue/RecoverPassword.vue/CheckTheEmail.vue) has no background of its
// own — it's bare inputs/text. Glassmorphism: a translucent, blurred card instead of the earlier
// flat opaque one, so the photo stays subtly visible through it.
.auth-card {
  position: relative;
  border-radius: var(--va-card-border-radius, 12px);
  padding: 2rem;

  // Fallback for browsers without backdrop-filter support (no blur means nothing behind the card
  // is softened, so it needs to be near-opaque to keep every form text color fully legible).
  // Overridden by the @supports block below wherever the browser can actually render blur —
  // backdrop-filter is widely supported today, but "widely" isn't "universal".
  background-color: color-mix(in srgb, var(--va-background-card-primary) 95%, transparent);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--va-text-primary) 12%, var(--va-background-border) 88%),
    0 8px 24px color-mix(in srgb, var(--va-text-primary) 15%, transparent);

  @supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)) {
    // 65% opaque card fill over a blurred backdrop — pushed down from 80% per feedback once the
    // panel overlay above was fixed (that fix is what actually made more transparency here safe:
    // before, this card's own translucency was compositing on top of an already near-opaque
    // overlay, so raising it further wouldn't have shown much more anyway). Blur bumped to 20px
    // (top of the 12-20px range) alongside the extra transparency specifically so a higher-detail
    // patch of the photo still gets smoothed enough for text to stay crisp — checked against the
    // busiest/brightest region behind the card, not just the flat token color, in both themes.
    background-color: color-mix(in srgb, var(--va-background-card-primary) 65%, transparent);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    // A translucent panel reads its own shape mostly from this edge rather than a background
    // color jump, so the ring gets a bit more presence here than the opaque fallback's — still a
    // hairline, not a heavy outline.
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--va-text-primary) 18%, transparent),
      0 8px 24px color-mix(in srgb, var(--va-text-primary) 15%, transparent);
  }

  // Vuestic's default unfocused border for both VaInput and VaCheckbox is 1px solid
  // var(--va-background-border) — fine against a flat white card, but here it's sitting on a
  // translucent/blurred surface that itself varies in tone, so that same faint line all but
  // disappears in places. Deep-selected (these are several component levels below this card, not
  // direct children) rather than touched globally, since the rest of the app's forms sit on plain
  // opaque cards where the default border already reads fine. Same color-mix() ratio as the row
  // divider in src/scss/vuestic.scss, for a consistent "clearly visible, not heavy" level.
  //
  // The checked checkbox is safe to target unconditionally — Vuestic sets its checked-state
  // border/background as an inline style (verified via computed style), which always wins over
  // any external CSS regardless of specificity. The input is NOT safe the same way: its
  // focused/error/success colors come from a CSS custom property one level up
  // (.va-input-wrapper--focused etc. redefining --va-input-wrapper-border-color on the wrapper),
  // consumed by the very same .va-input-wrapper__field rule this is overriding — so a plain
  // override here would win that too and flatten the focus ring to gray (reproduced and confirmed
  // before adding the :not() exclusions below). Excluding those wrapper states by class keeps this
  // scoped to true "resting" inputs only, leaving Vuestic's own focus/validation colors untouched.
  :deep(.va-checkbox__square) {
    border-color: color-mix(in srgb, var(--va-text-primary) 30%, var(--va-background-border) 70%);
  }

  :deep(
    .va-input-wrapper:not(.va-input-wrapper--focused):not(.va-input-wrapper--error):not(.va-input-wrapper--success)
      .va-input-wrapper__field
  ) {
    border-color: color-mix(in srgb, var(--va-text-primary) 30%, var(--va-background-border) 70%);
  }
}
</style>
